import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { ServiceBase } from './service-base';
import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationDto } from '../models/notification-dto';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { UserService } from './user.service';
import { QueryParameters } from '@planetact/models/query-parameters';
import { PagedResult } from '@planetact/models/paged-result';
@Injectable({
    providedIn: 'root'
})
export class NotificationService extends ServiceBase {
    private controllerRoute = `${this.baseRoute}/notification`;

    // Add new subject for unread notifications count
    private unreadNotificationsCountSubject = new BehaviorSubject<number>(0);
    public unreadNotificationsCount$ = this.unreadNotificationsCountSubject.asObservable();

    constructor(
        httpClient: HttpClient,
        notification: NzNotificationService,
        private ngZone: NgZone,
        private _userService: UserService
    ) {
        super(httpClient, notification);
    }

    getAll(): Observable<NotificationDto[]> {
        const url = `${this.controllerRoute}/getall`;
        return this.httpClient.get<NotificationDto[]>(url);
    }

    getById(notificationId: string): Observable<NotificationDto> {
        const url = `${this.controllerRoute}/getbyid/${notificationId}`;
        return this.httpClient.get<NotificationDto>(url);
    }

    getByUserId(userId: string): Observable<NotificationDto[]> {
        const url = `${this.controllerRoute}/getbyuserid/${userId}`;
        return this.httpClient.get<NotificationDto[]>(url);
    }

    create(notification: NotificationDto): Observable<NotificationDto> {
        const url = `${this.controllerRoute}/create`;
        return this.httpClient.post<NotificationDto>(url, notification);
    }

    markAsRead(notificationId: string): Observable<NotificationDto> {
        const url = `${this.controllerRoute}/markasread/${notificationId}`;
        return new Observable(observer => {
            this.httpClient.patch<NotificationDto>(url, {}).subscribe({
                next: (response) => {
                    this.updateUnreadNotificationsCount();
                    observer.next(response);
                    observer.complete();
                },
                error: (error) => observer.error(error)
            });
        });
    }

    delete(notificationId: string): Observable<NotificationDto> {
        const url = `${this.controllerRoute}/delete/${notificationId}`;
        return new Observable(observer => {
            this.httpClient.delete<NotificationDto>(url).subscribe({
                next: (response) => {
                    // After successful deletion, update the count
                    this.updateUnreadNotificationsCount();
                    observer.next(response);
                    observer.complete();
                },
                error: (error) => observer.error(error)
            });
        });
    }

    getMyNotifications(queryParams: QueryParameters): Observable<PagedResult<NotificationDto>> {
        const url = `${this.controllerRoute}/GetMyNotifications`;
        return this.httpClient.get<PagedResult<NotificationDto>>(url, { params: queryParams as any });
    }

    getMyNotificationsCount(): Observable<number> {
        const url = `${this.controllerRoute}/GetMyNotificationsCount`;
        return new Observable(observer => {
            this.httpClient.get<number>(url).subscribe({
                next: (count) => {
                    this.ngZone.run(() => {
                        this.unreadNotificationsCountSubject.next(count);
                        observer.next(count);
                        observer.complete();
                    });
                },
                error: (error) => observer.error(error)
            });
        });
    }

    getNotificationsCount(): Observable<number> {
        const url = `${this.controllerRoute}/GetNotificationsCount`;
        return new Observable(observer => {
            this.httpClient.get<number>(url).subscribe({
                next: (count) => {
                    this.ngZone.run(() => {
                        this.unreadNotificationsCountSubject.next(count);
                        observer.next(count);
                        observer.complete();
                    });
                },
                error: (error) => observer.error(error)
            });
        });
    }

    // Private helper method to update count
    private updateUnreadNotificationsCount(): void {
        if (this._userService.isAdmin()) {
            this.getNotificationsCount().subscribe();
        } else {
            this.getMyNotificationsCount().subscribe();
        }
    }

    getPagedAndFiltered(queryParams: QueryParameters): Observable<PagedResult<NotificationDto>> {
        const url = `${this.controllerRoute}/paged`;
        return this.httpClient.get<PagedResult<NotificationDto>>(url, { params: queryParams as any });
    }
} 